import useMutableRef from "hooks/common/useMutableRef";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export const createOptimizedContext = () => {
  const Context = React.createContext();

  const ContextProvider = ({ children, value, createActions, onUpdate }) => {
    const dataRef = useRef({ ...value, ...createActions(notify) });
    const listenersRef = useRef([]);
    const onUpdateRef = useMutableRef(onUpdate);
    const [{ listeners, data }] = useState({
      listeners: listenersRef,
      data: dataRef,
    });

    const notifyChange = useCallback(
      (update) => {
        const oldContext = data.current;
        if (!update) {
          data.current = {};
        } else {
          const updatedContext =
            typeof update === "function" ? update(data.current) : update;
          const newContext = { ...oldContext, ...updatedContext };
          data.current = newContext;
        }

        onUpdateRef.current?.(data.current);
        listeners.current.forEach((l) => l(oldContext, data.current));
      },
      [data, listeners, onUpdateRef]
    );

    function notify(d) {
      return notifyChange(d);
    }

    return (
      <Context.Provider
        value={{
          listeners,
          notifyChange,
          data,
        }}
      >
        {children}
      </Context.Provider>
    );
  };

  return { Context: Context, Provider: ContextProvider };
};

export const useOptimizedContext = (Context) => {
  const propKeys = useRef([]);
  const update = useState({})[1];
  const forceUpdate = useCallback(() => update({}), [update]);
  const { listeners, data, notifyChange } = useContext(Context);

  useEffect(() => {
    const listener = (oldContext, newContext) => {
      if (propKeys.current.some((key) => oldContext[key] !== newContext[key])) {
        forceUpdate();
      }
    };

    listeners.current = [...listeners.current, listener];

    return () =>
      (listeners.current = listeners.current.filter((l) => l !== listener));
  }, [listeners, forceUpdate, propKeys]);

  return new Proxy(
    {},
    {
      get: (_, name) => {
        if (!propKeys.current.includes(name)) {
          propKeys.current = [...propKeys.current, name];
        }

        if (name === "update") {
          return notifyChange;
        }

        return data.current[name];
      },
    }
  );
};
